import { useState } from "react";
import ScrollReveal from "@/components/ScrollReveal";
import { ChevronLeft, ChevronRight } from "lucide-react";

const STORIES = [
  {
    practice: "Northeast Cornea Center",
    challenge: "Persistent epithelial defect non-responsive to topical therapy after 6 weeks.",
    intervention: "Single XcellerEYES application via direct technique with bandage contact lens.",
    outcome: "Reepithelialization observed by week 2; patient discharged from acute treatment by week 4.",
    metrics: [
      { v: "2 wks", l: "To Reepith." },
      { v: "1×", l: "Application" },
      { v: "0", l: "Adverse Events" },
    ],
  },
  {
    practice: "Southeast Multi-Site Group",
    challenge: "Surgical Temporary Ocular Discomfort Syndrome (STODS) protocol disrupting cataract recovery.",
    intervention: "XcellerEYES integrated into pre/post-op workflow for high-risk patients.",
    outcome: "Reported reduction in post-op surface complaints and improved patient satisfaction scores.",
    metrics: [
      { v: "+22%", l: "Pt Satisfaction" },
      { v: "−35%", l: "Repeat Visits" },
      { v: "12 wk", l: "Audit Window" },
    ],
  },
  {
    practice: "West Coast Refractive Practice",
    challenge: "Need for predictable surface care in post-refractive ocular surface preparation.",
    intervention: "Adopted XcellerEYES as standardized adjunct in advanced ocular surface protocol.",
    outcome: "Simplified workflow with ambient inventory; eliminated cold-chain logistics.",
    metrics: [
      { v: "Ambient", l: "Storage" },
      { v: "0", l: "Freezers Req." },
      { v: "24 mo", l: "Shelf Life" },
    ],
  },
];

export default function SuccessStories() {
  const [idx, setIdx] = useState(0);
  const story = STORIES[idx];
  const next = () => setIdx((idx + 1) % STORIES.length);
  const prev = () => setIdx((idx - 1 + STORIES.length) % STORIES.length);

  return (
    <section data-testid="success-stories" className="bg-white py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="text-xs tracking-[0.22em] uppercase text-teal font-semibold">
                Provider Success Stories
              </div>
              <h2 className="mt-5 font-display text-4xl md:text-5xl text-slate1 leading-tight max-w-2xl">
                Real practices. Real workflows. Real results.
              </h2>
            </div>
            <div className="flex gap-3">
              <button
                onClick={prev}
                data-testid="stories-prev"
                className="h-12 w-12 rounded-full bg-cloud hover:bg-teal hover:text-white flex items-center justify-center transition-colors"
                aria-label="Previous"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={next}
                data-testid="stories-next"
                className="h-12 w-12 rounded-full bg-cloud hover:bg-teal hover:text-white flex items-center justify-center transition-colors"
                aria-label="Next"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal>
          <div
            data-testid={`story-card-${idx}`}
            className="rounded-3xl bg-cloud border border-slate1/5 overflow-hidden grid grid-cols-1 lg:grid-cols-12"
          >
            {/* Visual */}
            <div className="lg:col-span-5 bg-slate1 p-8 sm:p-10 md:p-12 text-white relative overflow-hidden flex flex-col justify-between min-h-[320px] sm:min-h-[400px]">
              <div className="absolute inset-0 grid-bg opacity-30" />
              <div className="relative">
                <div className="text-[10px] tracking-[0.22em] uppercase text-teal2 font-bold">
                  Case Study 0{idx + 1}
                </div>
                <div className="font-display text-2xl sm:text-3xl md:text-4xl font-medium mt-4">
                  {story.practice}
                </div>
              </div>
              <div className="relative grid grid-cols-3 gap-3 sm:gap-4 mt-8">
                {story.metrics.map((m) => (
                  <div key={m.l} className="border-l border-teal/40 pl-2.5 sm:pl-3 min-w-0">
                    <div className="font-display text-lg sm:text-xl md:text-2xl font-medium leading-tight break-words">{m.v}</div>
                    <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.14em] sm:tracking-[0.18em] text-white/55 mt-1.5 leading-snug">{m.l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Detail */}
            <div className="lg:col-span-7 p-8 sm:p-10 md:p-14">
              {[
                ["Challenge", story.challenge],
                ["Intervention", story.intervention],
                ["Outcome", story.outcome],
              ].map(([k, v]) => (
                <div key={k} className="mb-8 last:mb-0">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-teal">{k}</div>
                  <p className="mt-3 text-lg text-slate1 leading-relaxed">{v}</p>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>

        <div className="flex justify-center gap-2 mt-8">
          {STORIES.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`story-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                idx === i ? "w-10 bg-teal" : "w-2 bg-slate1/15 hover:bg-slate1/30"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
